// @ts-nocheck
/**
 * ドラフトシステム - パック生成
 */

import { DRAFT_PACK_COMPOSITION, DRAFT_RARITY_SLOTS } from '../constants';
import { valueCalculator } from '../../collection/systems/valueCalculator';

// ========================================
// レアリティ抽選
// ========================================

/**
 * レアリティスロットからレアリティを抽選
 * @param {string} slotType - 'C' | 'C_UC' | 'R_PLUS' | 'SR_PLUS'
 * @returns {string} レアリティ
 */
const drawRarity = (slotType) => {
  const rates = DRAFT_RARITY_SLOTS[slotType];
  if (!rates) return 'C';

  const rand = Math.random();
  let cumulative = 0;

  for (const [rarity, rate] of Object.entries(rates)) {
    cumulative += rate;
    if (rand < cumulative) {
      return rarity;
    }
  }

  // フォールバック
  return Object.keys(rates)[0];
};

// ========================================
// カードフィルタリング
// ========================================

/**
 * 条件に合うカードをフィルタリング
 * @param {Object[]} allCards - 全カードデータ
 * @param {Object} condition - 条件
 * @param {Map} cardValueMap - カード価値マップ
 * @param {string} rarity - 対象レアリティ
 * @param {Set} usedCardIds - 既に使用したカードID
 * @returns {Object[]} 条件に合うカード
 */
const filterCards = (allCards, condition, cardValueMap, rarity, usedCardIds) => {
  return allCards.filter(card => {
    // 既に使用済みは除外
    if (usedCardIds.has(card.id)) return false;

    // タイプチェック
    if (condition.type && card.type !== condition.type) return false;

    // コストチェック
    if (condition.costRange) {
      const cost = card.cost || 0;
      if (cost < condition.costRange[0] || cost > condition.costRange[1]) {
        return false;
      }
    }

    // レアリティチェック（価値情報から）
    const valueInfo = cardValueMap?.get(card.id);
    const tier = valueInfo?.tier || 'D';

    // GRは禁忌カードのみ
    if (rarity === 'GR') {
      return card.isForbidden ||
        (Array.isArray(card.keyword) && card.keyword.includes('禁忌カード'));
    }

    // ティアに基づくレアリティ判定
    return canAppearAtRarity(tier, rarity);
  });
};

/**
 * ティアがレアリティで出現可能かチェック
 * @param {string} tier - カードティア
 * @param {string} rarity - レアリティ
 * @returns {boolean}
 */
const canAppearAtRarity = (tier, rarity) => {
  // ティア→最低レアリティのマッピング
  const tierMinRarity = {
    S: 'R',
    A: 'UC',
    B: 'C',
    C: 'C',
    D: 'C',
  };

  const rarityOrder = ['C', 'UC', 'R', 'SR', 'UR', 'HR', 'SEC', 'ALT', 'SP', 'GR'];
  const minRarity = tierMinRarity[tier] || 'C';

  const minIndex = rarityOrder.indexOf(minRarity);
  const targetIndex = rarityOrder.indexOf(rarity);

  return targetIndex >= minIndex;
};

// ========================================
// カード抽選
// ========================================

/**
 * 条件に合うカードを1枚抽選
 * @param {Object[]} allCards - 全カードデータ
 * @param {Object} slotConfig - スロット設定
 * @param {Map} cardValueMap - カード価値マップ
 * @param {Set} usedCardIds - 既に使用したカードID
 * @returns {Object|null} 選ばれたカード
 */
const drawCardForSlot = (allCards, slotConfig, cardValueMap, usedCardIds) => {
  // レアリティを決定
  const rarity = drawRarity(slotConfig.rarity);

  // 条件でフィルタリング
  const condition = {
    type: slotConfig.type,
    costRange: slotConfig.costRange,
  };

  const eligibleCards = filterCards(allCards, condition, cardValueMap, rarity, usedCardIds);

  if (eligibleCards.length === 0) {
    // フォールバック: レアリティを下げて再試行
    const fallbackRarity = 'C';
    const fallbackCards = filterCards(allCards, condition, cardValueMap, fallbackRarity, usedCardIds);

    if (fallbackCards.length === 0) {
      // それでもなければ条件緩和
      const anyCards = allCards.filter(card =>
        !usedCardIds.has(card.id) && card.type === slotConfig.type
      );
      if (anyCards.length > 0) {
        const card = anyCards[Math.floor(Math.random() * anyCards.length)];
        return { ...card, rarity: 'C' };
      }
      return null;
    }

    const card = fallbackCards[Math.floor(Math.random() * fallbackCards.length)];
    return { ...card, rarity: fallbackRarity };
  }

  // ランダムに1枚選択
  const card = eligibleCards[Math.floor(Math.random() * eligibleCards.length)];
  return { ...card, rarity };
};

// ========================================
// パック生成
// ========================================

/**
 * ドラフトパックを1つ生成
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} [cardValueMap] - カード価値マップ（省略時は計算）
 * @returns {Object[]} パック内のカード（12枚）
 */
export const generateDraftPack = (allCards, cardValueMap = null) => {
  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  const pack = [];
  const usedCardIds = new Set();

  // モンスター枠（9枚）
  const monsterCards = allCards.filter(c => c.type === 'monster');
  for (const slot of DRAFT_PACK_COMPOSITION.monster.slots) {
    const card = drawCardForSlot(monsterCards, { ...slot, type: 'monster' }, valueMap, usedCardIds);
    if (card) {
      pack.push(card);
      usedCardIds.add(card.id);
    }
  }

  // 魔法枠（2枚）
  const magicCards = allCards.filter(c => c.type === 'magic');
  for (const slot of DRAFT_PACK_COMPOSITION.magic.slots) {
    const card = drawCardForSlot(magicCards, { ...slot, type: 'magic' }, valueMap, usedCardIds);
    if (card) {
      pack.push(card);
      usedCardIds.add(card.id);
    }
  }

  // フィールド/フェイズ枠（1枚）
  const isField = Math.random() < DRAFT_PACK_COMPOSITION.fieldOrPhase.fieldChance;
  const fieldPhaseType = isField ? 'field' : 'phase';
  const fieldPhaseCards = allCards.filter(c => c.type === fieldPhaseType);

  for (const slot of DRAFT_PACK_COMPOSITION.fieldOrPhase.slots) {
    const card = drawCardForSlot(fieldPhaseCards, { ...slot, type: fieldPhaseType }, valueMap, usedCardIds);
    if (card) {
      pack.push(card);
      usedCardIds.add(card.id);
    } else {
      // フィールド/フェイズがなければもう一方を試す
      const altType = isField ? 'phase' : 'field';
      const altCards = allCards.filter(c => c.type === altType);
      const altCard = drawCardForSlot(altCards, { ...slot, type: altType }, valueMap, usedCardIds);
      if (altCard) {
        pack.push(altCard);
        usedCardIds.add(altCard.id);
      }
    }
  }

  // パックをシャッフル
  return shuffleArray(pack);
};

/**
 * 複数のドラフトパックを生成
 * @param {Object[]} allCards - 全カードデータ
 * @param {number} count - 生成するパック数
 * @param {Map} [cardValueMap] - カード価値マップ
 * @returns {Object[][]} パックの配列
 */
export const generateDraftPacks = (allCards, count, cardValueMap = null) => {
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);
  const packs = [];

  for (let i = 0; i < count; i++) {
    packs.push(generateDraftPack(allCards, valueMap));
  }

  return packs;
};

/**
 * 配列をシャッフル
 * @param {Array} array - 配列
 * @returns {Array} シャッフルされた配列
 */
const shuffleArray = (array) => {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

export default {
  generateDraftPack,
  generateDraftPacks,
};
